// BUILD-046 task 8.18: React binding for the My Work host's lists (listsController.ts holds the logic and its tests).
//
// One controller per host. It re-renders the host when any list changes, and on a `caller-changed` event (the pilot persona switcher, the
// pilot Reset, or another tab switching the server-held persona) it clears and reloads every list that has a fetcher and discards the
// answers still in flight for the previous Caller (Req 2.7, 2.12).
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { onCallerChanged, workApi } from '../../api/workShared';
import { createListsController } from './listsController';
import type { ListsController } from './listsController';
import type { ListsState } from './listStateModel';
import type { RefreshNotes } from './listRefreshModel';

export interface WorkLists<T> {
  lists: ListsState<T>;
  controller: ListsController<T>;
  /** The lists whose silent re-read (when the Phoenix day turns) failed or is being retried; their rows are the last ones loaded. */
  refreshNotes: RefreshNotes;
}

export function useWorkLists<T>(): WorkLists<T> {
  const ref = useRef<ListsController<T> | null>(null);
  if (ref.current === null) ref.current = createListsController<T>({ getEpoch: () => workApi.getCallerEpoch() });
  const controller = ref.current;
  const lists = useSyncExternalStore(controller.subscribe, controller.getState);
  const refreshNotes = useSyncExternalStore(controller.subscribe, controller.getRefreshNotes);
  useEffect(
    () =>
      onCallerChanged(() => {
        void controller.callerChanged();
      }),
    [controller]
  );
  return { lists, controller, refreshNotes };
}
